import { useState } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d7ddcqs5q56ra7tufjo0';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function OfferFormSection() {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const notes = data.get('notes') as string;
    if (notes && notes.length > 500) {
      return;
    }

    setStatus('submitting');

    const encoded = new URLSearchParams();
    data.forEach((value, key) => {
      encoded.append(key, value as string);
    });

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encoded.toString(),
      });

      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="offer-form" className="py-20 md:py-28 bg-[#F4F9F8]">
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* Left — info panel */}
          <div className="lg:sticky lg:top-28">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600 mb-5">Free, No-Obligation</p>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-950 leading-tight mb-6">
              Get a Free<br />Written Offer
            </h2>
            <p className="text-gray-500 text-lg leading-relaxed mb-10">
              Fill in a few details about your property and we'll be in touch within one business day to arrange a visit. No agents, no open homes, no fees — ever.
            </p>

            <div className="space-y-5">
              {[
                { icon: 'ri-time-line', title: 'Response within 1 business day', desc: 'We\'ll call or email you to confirm a visit time that suits you.' },
                { icon: 'ri-home-4-line', title: 'We assess as-is', desc: 'No cleaning, staging, or repairs needed before we visit.' },
                { icon: 'ri-file-text-line', title: 'Written offer in 3 - 5 business days', desc: 'A formal written offer delivered after our assessment.' },
                { icon: 'ri-shield-check-line', title: 'Zero fees, zero obligation', desc: 'You\'re under no pressure to accept. The assessment is completely free.' },
              ].map((item) => (
                <div key={item.title} className="flex gap-4 items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-teal-700 text-lg`}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-base">{item.title}</p>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — form */}
          <div className="bg-white rounded-2xl p-8 md:p-10">
            {status === 'success' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-6">
                  <i className="ri-check-line text-teal-700 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">We've received your details!</h3>
                <p className="text-gray-500 text-base leading-relaxed mb-8">
                  One of our team will be in touch within one business day to arrange a property visit at a time that suits you.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="px-6 py-3 bg-teal-700 text-white font-semibold rounded-full hover:bg-teal-800 transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  Submit Another Enquiry
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="seller-offer-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Your Property Details</h3>
                  <p className="text-gray-400 text-sm">All fields marked * are required.</p>
                </div>

                {/* Name row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="first_name">
                      First Name *
                    </label>
                    <input
                      id="first_name"
                      name="first_name"
                      type="text"
                      required
                      placeholder="Jane"
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="last_name">
                      Last Name *
                    </label>
                    <input
                      id="last_name"
                      name="last_name"
                      type="text"
                      required
                      placeholder="Smith"
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                    />
                  </div>
                </div>

                {/* Contact row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="email">
                      Email Address *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="jane@example.com"
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="phone">
                      Phone Number *
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder="04XX XXX XXX"
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                    />
                  </div>
                </div>

                {/* Property address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="property_address">
                    Property Address *
                  </label>
                  <input
                    id="property_address"
                    name="property_address"
                    type="text"
                    required
                    placeholder="123 Example Street, Suburb VIC 3000"
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                  />
                </div>

                {/* Property type + bedrooms */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="property_type">
                      Property Type *
                    </label>
                    <select
                      id="property_type"
                      name="property_type"
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition bg-white cursor-pointer"
                    >
                      <option value="">Select type...</option>
                      <option value="House">House</option>
                      <option value="Townhouse">Townhouse</option>
                      <option value="Apartment / Unit">Apartment / Unit</option>
                      <option value="Villa">Villa</option>
                      <option value="Land">Land</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="bedrooms">
                      Bedrooms
                    </label>
                    <select
                      id="bedrooms"
                      name="bedrooms"
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition bg-white cursor-pointer"
                    >
                      <option value="">Select...</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5+">5+</option>
                    </select>
                  </div>
                </div>

                {/* Selling reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="selling_reason">
                    Reason for Selling
                  </label>
                  <select
                    id="selling_reason"
                    name="selling_reason"
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition bg-white cursor-pointer"
                  >
                    <option value="">Select reason...</option>
                    <option value="Downsizing">Downsizing</option>
                    <option value="Upgrading">Upgrading</option>
                    <option value="Relocation">Relocation</option>
                    <option value="Moving to care home">Moving to care home</option>
                    <option value="Divorce / property settlement">Divorce / property settlement</option>
                    <option value="Financial pressure">Financial pressure</option>
                    <option value="Deceased estate">Deceased estate</option>
                    <option value="Investment property sale">Investment property sale</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Timeline */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="timeline">
                    Ideal Settlement Timeline
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition bg-white cursor-pointer"
                  >
                    <option value="">Select timeline...</option>
                    <option value="ASAP (within 30 days)">ASAP (within 30 days)</option>
                    <option value="1–3 months">1–3 months</option>
                    <option value="3–6 months">3–6 months</option>
                    <option value="Flexible">Flexible</option>
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="notes">
                    Anything else we should know?
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    rows={4}
                    maxLength={500}
                    placeholder="E.g. property condition, tenants in place, access notes..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition resize-none"
                  />
                  <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-red-500' : 'text-gray-400'}`}>
                    {charCount}/500
                  </p>
                </div>

                {status === 'error' && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <i className="ri-error-warning-line text-red-500 text-base"></i>
                    <p className="text-sm text-red-600">Something went wrong. Please try again or call us directly.</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full py-4 bg-teal-700 hover:bg-teal-800 disabled:bg-teal-400 text-white font-semibold rounded-full transition-colors cursor-pointer whitespace-nowrap text-base flex items-center justify-center gap-2"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      Request My Free Assessment
                      <i className="ri-arrow-right-line"></i>
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center leading-relaxed">
                  By submitting this form you agree to be contacted by ReadyBuyer Group regarding your property enquiry. We never share your details with third parties.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
